import { useCallback, useEffect, useState } from "react";
import { Stack, Typography, Divider, Grid } from "@mui/material";
import {
  MethodPicker,
  Switch,
} from "../../../../../utils/form";
import _ from "lodash";
import NavigationBar from "../../../../../utils/wizard/navigation";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import TemplatesMenu from "../../../../utils/medicalDocument/templates/menu";
import { useApi } from "../../../../../utils/hooks";
import AutocompleteMedicam from "./vademecum/AutocompleteMedicam";
import CardSeleted from "../../../../utils/medicalDocument/card/CardSeleted";
import { ErrorAlert } from "../../../../../utils";
import { useAuth } from "../../../../../auth/context";
import { availablePrescriptionMethods, MANUAL, VADEMECUM } from "./prescriptionMethods";

const templateType = "prescription";
const defaultMedicine = {
  template: "",
  text: "",
  longTerm: false,
  quantity: 1,
  externalId: null,
  requiresDuplicate: false,
  genericOnly: false,
  posology: "",
  brandRecommendation: false,
};

const LongTermCheckbox = ({ form }) => {
  return (
    <Switch
      form={form}
      name={"medicines[0].longTerm"}
      label="Tratamiento prolongado"
      size={12}
    />
  );
};

const HivCheckbox = ({ form }) => {
  return <Switch form={form} name="hiv" label="Paciente HIV" size={12} />;
};

const MedicineForm = ({ onFinish, fullForm, ...props }) => {
  const { user } = useAuth();
  const methodOptions = availablePrescriptionMethods(user.prescriptionMethods);
  const [method, setMethod] = useState(_.head(methodOptions)?.value ?? MANUAL);
  const [inputValue, setInputValue] = useState("");
  const [medicineIndex, setMedicineIndex] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [disabledAddMedicine, setDisabledAddMedicine] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const api = useApi();
  const defaultTemplateContent = defaultMedicine.text;
  const form = useForm({
    defaultValues: { medicines: [defaultMedicine], hiv: false },
  });
  const { handleSubmit, control, reset, getValues, watch } = form;
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "medicines",
  });

  const medicines = getValues("medicines");
  const currentForm = watch();

  useEffect(() => {
    const isDisabled =
      medicines.length === 3 && (medicines[2]?.text ?? "") !== "";

    setDisabledAddMedicine(isDisabled);
  }, [medicines, medicines[2]?.text]);

  const addMedicine = () => {
    if (method === MANUAL) {
      if (
        medicines.length > 0 &&
        medicines.some(
          (medicine) => medicine.text === "" || medicine.text === "\n"
        )
      ) {
        return;
      } else {
        append(_.cloneDeep(defaultMedicine));
      }
    } else {
      if (medicines.length > 0 && !medicines[medicines.length - 1].text) {
        return;
      }
      append(_.cloneDeep(defaultMedicine));
    }
  };

  const someMedicineSelected = _.some(
    currentForm.medicines,
    (medicine) => medicine.text
  );
  const canContinue = someMedicineSelected;

  useEffect(() => {
    api.templates(templateType).catch(() => []).tap(setTemplates);
  }, []);

  const onSubmit = useCallback(
    ({ medicines: allMedicines, hiv }) => {
      const longTermValue = allMedicines[0]?.longTerm || false;

      const updatedMedicines = allMedicines.map((medicine) => {
        return {
          ...defaultMedicine,
          ...medicine,
          longTerm: longTermValue,
        };
      });

      onFinish({ method, medicines: updatedMedicines, hiv });
      props.nextStep();
    },
    [method]
  );

  const handleChangeTabs = (event, newValue) => {
    const defaultValues = {
      medicines: [defaultMedicine],
    };
    setMethod(newValue);
    reset(defaultValues);
    setSelectedTemplate(null);
    if (newValue === MANUAL) {
      setOpenWarning(true);
    } else {
      setOpenWarning(false);
    }
  };

  const onAlertClose = () => setOpenWarning(null);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Stack spacing="1rem">
          <Typography
            variant="title"
            sx={{
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            Medicamentos
          </Typography>
          {methodOptions.length > 1 && (
            <MethodPicker
              method={method}
              options={methodOptions}
              handleChangeTabs={handleChangeTabs}
            />
          )}

          {method === VADEMECUM && (
            <Stack mt={2}>
              <AutocompleteMedicam
                inputValue={inputValue}
                setInputValue={setInputValue}
                medicineIndex={medicineIndex}
                setMedicineIndex={setMedicineIndex}
                addMedicine={addMedicine}
                disabledAddMedicine={disabledAddMedicine}
              />
            </Stack>
          )}

          {method === MANUAL && (
            <TemplatesMenu
              templates={templates}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              defaultContent={defaultTemplateContent}
              type={templateType}
              addMedicine={addMedicine}
              form={form}
              defaultMedicine={defaultMedicine}
              disabledAddMedicine={disabledAddMedicine}
              canCreate
            />
          )}

          <Stack>
            <Grid container spacing={2} minHeight={260}>
              {_(fields)
                .map((field, index) => (
                  <Grid item xs={12} sm={12} md={6} key={field.id}>
                    <CardSeleted
                      form={form}
                      name={`medicines[${index}]`}
                      method={method}
                      required={index === 0}
                      medicineIndex={medicineIndex}
                      setMedicineIndex={setMedicineIndex}
                      remove={remove}
                      index={index}
                      fields={fields}
                      selectedTemplate={selectedTemplate}
                      setSelectedTemplate={setSelectedTemplate}
                      lengthMedicines={medicines.length}
                      templateType={templateType}
                      defaultTemplateText={method === MANUAL && "Nuevo medicamento"}
                    />
                  </Grid>
                ))
                .value()}
            </Grid>

            <Divider sx={{ mt: 2, mb: 2 }} />

            <Stack
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <HivCheckbox form={form} />
              <LongTermCheckbox form={form} />
            </Stack>
          </Stack>
        </Stack>

        <Stack pb="1rem">
          <NavigationBar
            nextDisabled={!canContinue}
            {...props}
          />
        </Stack>
        <ErrorAlert
          show={openWarning}
          onClose={onAlertClose}
          message={
            "Las recetas manuales deben ser utilizadas para recetas magistrales y medicamentos fuera del Vademecum. Sino, serán rechazadas en farmacia"
          }
          severity="warning"
          alwaysVisible={true}
        />
      </form>
    </FormProvider>
  );
};

export default MedicineForm;
